import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { usePaymentMethods, useUpdatePaymentMethod } from '@/hooks/usePaymentMethods';
import { usePermissions } from '@/hooks/usePermissions';
import { useCustomerPaymentMethods } from '@/hooks/useCustomerPaymentMethods';
import { useClientOnboardingStatus } from '@/hooks/useClientOnboardingStatus';
import { CreditCard, RefreshCw, AlertCircle, Building2, MoreVertical, Plus, Trash2, UserCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';
interface PaymentMethodManagerProps {
  organizationId: string;
}

export function PaymentMethodManager({ organizationId }: PaymentMethodManagerProps) {
  const { canManagePayments } = usePermissions();
  const { data: legacyPaymentMethods, isLoading: legacyLoading } = usePaymentMethods(organizationId);
  const { 
    paymentMethods, 
    isLoading: methodsLoading, 
    setDefault, 
    setDefaultPending,
    deletePaymentMethod,
    deletePending,
    syncFromStripe,
    syncPending
  } = useCustomerPaymentMethods(organizationId);
  const { data: onboardingStatus, isLoading: onboardingLoading } = useClientOnboardingStatus();
  const { mutate: updatePaymentMethod, isPending } = useUpdatePaymentMethod();
  const { toast } = useToast();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedMethodToDelete, setSelectedMethodToDelete] = useState<string | null>(null);

  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  if (flagLoading || !paymentProcessingEnabled) return null;

  const profilesComplete = onboardingStatus?.userProfileComplete && onboardingStatus?.companyProfileComplete;

  const handleAddPaymentMethod = () => {
    const currentUrl = window.location.href;
    
    updatePaymentMethod({
      organizationId,
      successUrl: `${currentUrl}?payment_method_updated=true`,
      cancelUrl: currentUrl,
    });
  };

  const handleSetDefault = (paymentMethodId: string) => {
    setDefault(paymentMethodId);
  };

  const handleDeleteClick = (paymentMethodId: string) => {
    setSelectedMethodToDelete(paymentMethodId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedMethodToDelete) {
      deletePaymentMethod(selectedMethodToDelete);
      setDeleteDialogOpen(false);
      setSelectedMethodToDelete(null);
    }
  };

  if (legacyLoading || methodsLoading || onboardingLoading) {
    return <div>Loading payment methods...</div>;
  }

  if (!canManagePayments) {
    return (
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Only organization owners and super users can manage payment methods.
          Please contact your organization owner for access.
        </AlertDescription>
      </Alert>
    );
  }

  const hasPaymentMethod = paymentMethods && paymentMethods.length > 0;
  const hasAchPayment = paymentMethods?.some(pm => pm.payment_method_type === 'us_bank_account');

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Payment Methods</CardTitle>
              <CardDescription>Manage your organization's payment methods</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              {hasPaymentMethod && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => syncFromStripe()}
                  disabled={syncPending}
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncPending ? 'animate-spin' : ''}`} />
                  Sync from Stripe
                </Button>
              )}
              <Badge variant={hasPaymentMethod ? 'default' : 'destructive'}>
                {hasPaymentMethod ? `${paymentMethods.length} Active` : 'Not Set'}
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasPaymentMethod ? (
            <div className="space-y-3">
              {paymentMethods.map((pm) => (
                <div 
                  key={pm.id} 
                  className="flex items-center justify-between p-4 rounded-lg border bg-muted/50"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10">
                      {pm.payment_method_type === 'us_bank_account' ? (
                        <Building2 className="h-5 w-5 text-primary" />
                      ) : (
                        <CreditCard className="h-5 w-5 text-primary" />
                      )}
                    </div>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        {pm.payment_method_type === 'us_bank_account' 
                          ? `${pm.bank_name || 'Bank Account'} ••••${pm.payment_method_last4}`
                          : `${pm.card_brand?.toUpperCase() || 'Card'} ••••${pm.payment_method_last4}`}
                        {pm.is_default && <Badge variant="secondary" className="text-xs">Default</Badge>}
                      </div>
                      {pm.nickname && (
                        <div className="text-sm text-muted-foreground">{pm.nickname}</div>
                      )}
                      <div className="text-sm text-muted-foreground">
                        {pm.payment_method_type === 'us_bank_account'
                          ? 'ACH bank transfer - settlements take 3-5 business days'
                          : 'Ready to use for transactions'}
                      </div>
                    </div>
                  </div>
                  
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" disabled={setDefaultPending || deletePending}>
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {!pm.is_default && (
                        <DropdownMenuItem onClick={() => handleSetDefault(pm.id)}>
                          Set as Default
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem 
                        onClick={() => handleDeleteClick(pm.id)}
                        className="text-destructive focus:text-destructive"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-between p-4 rounded-lg border border-destructive/50 bg-destructive/5">
              <div>
                <div className="font-medium text-destructive">No payment methods</div>
                <div className="text-sm text-muted-foreground">
                  Add a payment method to continue with transactions
                </div>
              </div>
            </div>
          )}

          {/* ACH Warning */}
          {hasAchPayment && (
            <Alert>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                <strong>ACH Payment Note:</strong> Bank transfers typically take 3-5 business days to settle. 
                You won't be able to issue Purchase Orders until the payment clears.
              </AlertDescription>
            </Alert>
          )}

          {/* Profile Completion Warning */}
          {!profilesComplete && onboardingStatus && (
            <Alert className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
              <UserCircle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800 dark:text-amber-200">
                <strong>Complete your profile first.</strong> To add a payment method, please complete your{' '}
                {!onboardingStatus.userProfileComplete && (
                  <Link to="/client/profile" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100">user profile</Link>
                )}
                {!onboardingStatus.userProfileComplete && !onboardingStatus.companyProfileComplete && ' and '}
                {!onboardingStatus.companyProfileComplete && (
                  <Link to="/client/company" className="underline font-medium hover:text-amber-900 dark:hover:text-amber-100">company profile</Link>
                )}
                . This information will be used to set up your billing account.
              </AlertDescription>
            </Alert>
          )}

          <Button
            onClick={handleAddPaymentMethod}
            disabled={isPending || !profilesComplete}
            className="w-full gap-2"
            variant="outline"
          >
            <Plus className="h-4 w-4" />
            {isPending ? 'Redirecting...' : 'Add New Payment Method'}
          </Button>

          <div className="text-xs text-muted-foreground space-y-1">
            <p>• Payments are processed securely through Stripe</p>
            <p>• You can add credit/debit cards or bank accounts (ACH)</p>
            <p>• Your payment information is never stored on our servers</p>
            <p>• You can have multiple payment methods and set one as default</p>
          </div>
        </CardContent>
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Payment Method</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this payment method? This action cannot be undone.
              {paymentMethods?.find(pm => pm.id === selectedMethodToDelete)?.is_default && (
                <span className="block mt-2 text-destructive font-medium">
                  Note: This is your default payment method. Another payment method will be set as default automatically.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
